/**
 * BlueprintAmbitionPanel — editable one-line executive ambition that headlines
 * the Target Operating Process Blueprint (DOCX). Auto-prefilled by the backend
 * with a sensible default when left blank; locked once the report is finalised.
 */
import React, { useEffect, useState } from "react";
import { Target, ChevronDown, ChevronUp, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function BlueprintAmbitionPanel({ runId, run, onChange }) {
  const [open, setOpen] = useState(false);
  const [ambition, setAmbition] = useState("");
  const [busy, setBusy] = useState(false);
  const locked = !!run?.finalised;

  useEffect(() => { setAmbition(run?.blueprint_ambition || ""); }, [run?.blueprint_ambition]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/assessments/runs/${runId}/blueprint-ambition`, { blueprint_ambition: ambition.trim() });
      toast.success("Ambition saved — shown across deliverables");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save ambition");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="blueprint-ambition-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2" data-testid="ambition-toggle">
        <span className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
          <Target size={14}/> Blueprint ambition {ambition?.trim() && <span className="text-[#7737F6] normal-case tracking-normal">· set</span>}
        </span>
        {open ? <ChevronUp size={16} className="text-zinc-400"/> : <ChevronDown size={16} className="text-zinc-400"/>}
      </button>

      {open && (
        <div className="mt-4 space-y-3" data-testid="ambition-form">
          {locked && (
            <div className="flex items-center gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-2" data-testid="ambition-locked">
              <Lock size={12}/> Report is finalised — the ambition is locked. Re-finalise to change.
            </div>
          )}
          <p className="text-[11px] text-zinc-500 leading-relaxed">A one-line, client-specific ambition shown on the cover of every deliverable (Blueprint, Improvement Plan, Board Q&A and the Business case). Leave blank to auto-generate a sensible default in the Blueprint.</p>
          <textarea
            rows={3}
            value={ambition}
            onChange={(e) => setAmbition(e.target.value)}
            disabled={locked}
            maxLength={600}
            placeholder="e.g. Become the benchmark for IT service in our sector within 12 months."
            className="w-full border border-zinc-300 px-2.5 py-2 text-[13px] disabled:bg-zinc-100"
            data-testid="ambition-input"
          />
          <div className="text-[10.5px] text-zinc-400 text-right">{ambition.length}/600</div>
          <button type="button" onClick={save} disabled={busy || locked} className="ek-btn ek-btn-primary w-full text-[13px] disabled:opacity-50" data-testid="ambition-save">
            <Save size={13}/> {busy ? "Saving…" : "Save ambition"}
          </button>
        </div>
      )}
    </div>
  );
}
